/** @format */

import { createSlice } from "@reduxjs/toolkit";
import {
	// Actions
	getPayoutOverviewAsAdmin,
	getRetryablePayoutsAsAdmin,
	retryPayoutsForDate,
	retrySpecificPayout,
	retryAllPayouts,
	getPayoutsByCircleAsAdmin,
	getPayoutsByMemberAsAdmin,
	getPayoutsByCycleAsAdmin,
	getCirclePayoutDetailsAsAdmin,
	// Types
	PayoutOverviewResponse,
	RetryablePayoutsResponse,
	RetryPayoutResult,
	BulkRetryResult,
	PayoutRecord,
	CirclePayoutDetailsResponse,
} from "../actions/adminPayOutAction";

// ==================== State Interface ====================

export interface PayoutsByFilterData {
	summary: {
		total: number;
		completed: number;
		missed: number;
		failed: number;
		dueToday: number;
		upcoming: number;
	};
	payouts: {
		completed: PayoutRecord[];
		missed: PayoutRecord[];
		failed: PayoutRecord[];
		dueToday: PayoutRecord[];
		upcoming: PayoutRecord[];
		all: PayoutRecord[];
	};
}

export interface AdminPayoutState {
	// Payout Overview
	payoutOverview: PayoutOverviewResponse | null;
	isLoadingOverview: boolean;
	overviewError: string | null;

	// Retryable Payouts
	retryablePayouts: RetryablePayoutsResponse | null;
	isLoadingRetryable: boolean;
	retryableError: string | null;

	// Retry Operations
	isRetrying: boolean;
	lastRetryResult: RetryPayoutResult | null;
	bulkRetryResult: BulkRetryResult | null;
	retryError: string | null;

	// Filtered Payouts by Circle
	payoutsByCircle: { [circleId: string]: PayoutsByFilterData | null };
	isLoadingByCircle: boolean;
	byCircleError: string | null;

	// Filtered Payouts by Member
	payoutsByMember: { [memberId: string]: PayoutsByFilterData | null };
	isLoadingByMember: boolean;
	byMemberError: string | null;

	// Filtered Payouts by Cycle
	payoutsByCycle: { [cycleId: string]: PayoutsByFilterData | null };
	isLoadingByCycle: boolean;
	byCycleError: string | null;

	// Circle Payout Details (for modal with calendar)
	circlePayoutDetails: {
		[circleId: string]: CirclePayoutDetailsResponse | null;
	};
	isLoadingCircleDetails: boolean;
	circleDetailsError: string | null;

	// General state
	isLoading: boolean;
	error: string | null;
}

// ==================== Initial State ====================

const initialState: AdminPayoutState = {
	// Payout Overview
	payoutOverview: null,
	isLoadingOverview: false,
	overviewError: null,

	// Retryable Payouts
	retryablePayouts: null,
	isLoadingRetryable: false,
	retryableError: null,

	// Retry Operations
	isRetrying: false,
	lastRetryResult: null,
	bulkRetryResult: null,
	retryError: null,

	// Filtered Payouts by Circle
	payoutsByCircle: {},
	isLoadingByCircle: false,
	byCircleError: null,

	// Filtered Payouts by Member
	payoutsByMember: {},
	isLoadingByMember: false,
	byMemberError: null,

	// Filtered Payouts by Cycle
	payoutsByCycle: {},
	isLoadingByCycle: false,
	byCycleError: null,

	// Circle Payout Details (for modal with calendar)
	circlePayoutDetails: {},
	isLoadingCircleDetails: false,
	circleDetailsError: null,

	// General state
	isLoading: false,
	error: null,
};

// ==================== Slice ====================

const adminPayoutSlice = createSlice({
	name: "adminPayout",
	initialState,
	reducers: {
		// Clear all errors
		clearPayoutErrors: (state) => {
			state.error = null;
			state.overviewError = null;
			state.retryableError = null;
			state.retryError = null;
			state.byCircleError = null;
			state.byMemberError = null;
			state.byCycleError = null;
			state.circleDetailsError = null;
		},
		// Clear retry results
		clearRetryResults: (state) => {
			state.lastRetryResult = null;
			state.bulkRetryResult = null;
			state.retryError = null;
		},
		// Reset payout overview
		resetPayoutOverview: (state) => {
			state.payoutOverview = null;
			state.overviewError = null;
		},
		// Reset retryable payouts
		resetRetryablePayouts: (state) => {
			state.retryablePayouts = null;
			state.retryableError = null;
		},
	},
	extraReducers: (builder) => {
		// ==================== Get Payout Overview ====================
		builder
			.addCase(getPayoutOverviewAsAdmin.pending, (state) => {
				state.isLoadingOverview = true;
				state.isLoading = true;
				state.overviewError = null;
			})
			.addCase(getPayoutOverviewAsAdmin.fulfilled, (state, action) => {
				state.isLoadingOverview = false;
				state.isLoading = false;
				state.payoutOverview = action.payload?.data || null;
			})
			.addCase(getPayoutOverviewAsAdmin.rejected, (state, action) => {
				state.isLoadingOverview = false;
				state.isLoading = false;
				state.overviewError = action.payload as string;
			});

		// ==================== Get Retryable Payouts ====================
		builder
			.addCase(getRetryablePayoutsAsAdmin.pending, (state) => {
				state.isLoadingRetryable = true;
				state.isLoading = true;
				state.retryableError = null;
			})
			.addCase(getRetryablePayoutsAsAdmin.fulfilled, (state, action) => {
				state.isLoadingRetryable = false;
				state.isLoading = false;
				state.retryablePayouts = action.payload?.data || null;
			})
			.addCase(getRetryablePayoutsAsAdmin.rejected, (state, action) => {
				state.isLoadingRetryable = false;
				state.isLoading = false;
				state.retryableError = action.payload as string;
			});

		// ==================== Retry Payouts For Date ====================
		builder
			.addCase(retryPayoutsForDate.pending, (state) => {
				state.isRetrying = true;
				state.isLoading = true;
				state.retryError = null;
				state.lastRetryResult = null;
			})
			.addCase(retryPayoutsForDate.fulfilled, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				state.lastRetryResult = action.payload?.data || null;
			})
			.addCase(retryPayoutsForDate.rejected, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				// Handle structured error object from rejectWithValue
				const payload = action.payload as
					| { message: string; details?: any; success: boolean }
					| string;
				if (typeof payload === "object" && payload !== null) {
					// Store the error as a retry result so UI can display it with details
					state.lastRetryResult = {
						success: false,
						date: new Date().toISOString(),
						cyclesChecked: 0,
						payoutsProcessed: 0,
						payoutsFailed: 1,
						message: payload.message,
						details: payload.details || undefined,
					};
					state.retryError = payload.message;
				} else {
					state.retryError = payload || "Failed to retry payouts";
				}
			});

		// ==================== Retry Specific Payout ====================
		builder
			.addCase(retrySpecificPayout.pending, (state) => {
				state.isRetrying = true;
				state.isLoading = true;
				state.retryError = null;
				state.lastRetryResult = null;
			})
			.addCase(retrySpecificPayout.fulfilled, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				state.lastRetryResult = action.payload?.data || null;
			})
			.addCase(retrySpecificPayout.rejected, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				// Handle structured error object from rejectWithValue
				const payload = action.payload as
					| { message: string; details?: any; success: boolean }
					| string;
				if (typeof payload === "object" && payload !== null) {
					// Store the error as a retry result so UI can display it with details
					state.lastRetryResult = {
						success: false,
						date: new Date().toISOString(),
						cyclesChecked: 0,
						payoutsProcessed: 0,
						payoutsFailed: 1,
						message: payload.message,
						details: payload.details || undefined,
					};
					state.retryError = payload.message;
				} else {
					state.retryError = payload || "Failed to retry payout";
				}
			});

		// ==================== Retry All Payouts ====================
		builder
			.addCase(retryAllPayouts.pending, (state) => {
				state.isRetrying = true;
				state.isLoading = true;
				state.retryError = null;
				state.bulkRetryResult = null;
			})
			.addCase(retryAllPayouts.fulfilled, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				state.bulkRetryResult = action.payload?.data || null;
			})
			.addCase(retryAllPayouts.rejected, (state, action) => {
				state.isRetrying = false;
				state.isLoading = false;
				state.retryError = action.payload as string;
			});

		// ==================== Get Payouts By Circle ====================
		builder
			.addCase(getPayoutsByCircleAsAdmin.pending, (state) => {
				state.isLoadingByCircle = true;
				state.isLoading = true;
				state.byCircleError = null;
			})
			.addCase(getPayoutsByCircleAsAdmin.fulfilled, (state, action) => {
				state.isLoadingByCircle = false;
				state.isLoading = false;
				const circleId = action.payload?.data?.circleId;
				if (circleId) {
					if (!state.payoutsByCircle) {
						state.payoutsByCircle = {};
					}
					state.payoutsByCircle[circleId] = {
						summary: action.payload.data.summary,
						payouts: action.payload.data.payouts,
					};
				}
			})
			.addCase(getPayoutsByCircleAsAdmin.rejected, (state, action) => {
				state.isLoadingByCircle = false;
				state.isLoading = false;
				state.byCircleError = action.payload as string;
			});

		// ==================== Get Payouts By Member ====================
		builder
			.addCase(getPayoutsByMemberAsAdmin.pending, (state) => {
				state.isLoadingByMember = true;
				state.isLoading = true;
				state.byMemberError = null;
			})
			.addCase(getPayoutsByMemberAsAdmin.fulfilled, (state, action) => {
				state.isLoadingByMember = false;
				state.isLoading = false;
				const memberId = action.payload?.data?.memberId;
				if (memberId) {
					if (!state.payoutsByMember) {
						state.payoutsByMember = {};
					}
					state.payoutsByMember[memberId] = {
						summary: action.payload.data.summary,
						payouts: action.payload.data.payouts,
					};
				}
			})
			.addCase(getPayoutsByMemberAsAdmin.rejected, (state, action) => {
				state.isLoadingByMember = false;
				state.isLoading = false;
				state.byMemberError = action.payload as string;
			});

		// ==================== Get Payouts By Cycle ====================
		builder
			.addCase(getPayoutsByCycleAsAdmin.pending, (state) => {
				state.isLoadingByCycle = true;
				state.isLoading = true;
				state.byCycleError = null;
			})
			.addCase(getPayoutsByCycleAsAdmin.fulfilled, (state, action) => {
				state.isLoadingByCycle = false;
				state.isLoading = false;
				const cycleId = action.payload?.data?.cycleId;
				if (cycleId) {
					if (!state.payoutsByCycle) {
						state.payoutsByCycle = {};
					}
					state.payoutsByCycle[cycleId] = {
						summary: action.payload.data.summary,
						payouts: action.payload.data.payouts,
					};
				}
			})
			.addCase(getPayoutsByCycleAsAdmin.rejected, (state, action) => {
				state.isLoadingByCycle = false;
				state.isLoading = false;
				state.byCycleError = action.payload as string;
			});

		// ==================== Get Circle Payout Details (Modal with Calendar) ====================
		builder
			.addCase(getCirclePayoutDetailsAsAdmin.pending, (state) => {
				state.isLoadingCircleDetails = true;
				state.isLoading = true;
				state.circleDetailsError = null;
			})
			.addCase(getCirclePayoutDetailsAsAdmin.fulfilled, (state, action) => {
				state.isLoadingCircleDetails = false;
				state.isLoading = false;
				const circleId = action.payload?.data?.circleId;
				if (circleId) {
					// Ensure circlePayoutDetails object exists before setting property
					if (!state.circlePayoutDetails) {
						state.circlePayoutDetails = {};
					}
					state.circlePayoutDetails[circleId] = action.payload.data;
				}
			})
			.addCase(getCirclePayoutDetailsAsAdmin.rejected, (state, action) => {
				state.isLoadingCircleDetails = false;
				state.isLoading = false;
				state.circleDetailsError = action.payload as string;
			});
	},
});

// ==================== Exports ====================

export const {
	clearPayoutErrors,
	clearRetryResults,
	resetPayoutOverview,
	resetRetryablePayouts,
} = adminPayoutSlice.actions;

export default adminPayoutSlice.reducer;
